import React, { lazy, Suspense } from 'react';
import { Loader } from 'components/Base';

import NotFound from 'pages/NotFound';

import useApplicationStep from 'utils/useApplicationStep';

import { useApplicationData } from 'utils/useApplicationData';

import { ApplicationInformation } from 'api/rtkQueryApi';

import { ApplicationStep } from 'handlers/stages';

import NoActiveApplication from './steps/NoActiveApplication';

const Overview = lazy(() => import('./steps/Overview'));
const ImportantInformation = lazy(() => import('./steps/ImportantInformation'));
const BankAccountVerificationSelectMethod = lazy(() => import('./steps/BankAccountVerificationSelectMethod'));
const BankAccountVerificationVerifyConsent = lazy(() =>
  import('./steps/BankAccountVerificationConsent/BankAccountVerificationVerifyConsent'),
);
const BankAccountVerificationLoginConsent = lazy(() =>
  import('./steps/BankAccountVerificationConsent/BankAccountVerificationLoginConsent'),
);
const BankAccountVerificationLoginWidget = lazy(() => import('./steps/BankAccountVerificationLoginWidget'));
const BankAccountVerificationEnterInformation = lazy(() => import('./steps/BankAccountVerificationEnterInformation'));
const IncomeSources = lazy(() => import('./steps/IncomeSources'));
const DocumentsUpload = lazy(() => import('./steps/DocumentsUpload'));
const SubmitApplication = lazy(() => import('./steps/SubmitApplication'));
const ThankYou = lazy(() => import('./steps/ThankYou'));
const LoanApproved = lazy(() => import('./steps/LoanApproved'));
const UnderReview = lazy(() => import('./steps/UnderReview'));
const LoanComplete = lazy(() => import('./steps/FundingDetails'));
const ApplicationRejected = lazy(() => import('./steps/ApplicationRejected'));
const AdditionalDocumentsUpload = lazy(() => import('./steps/AdditionalDocumentsUpload'));
const ExpiredApplication = lazy(() => import('./steps/ExpiredApplication'));
const BankAccountVerificationNotAbleToVerify = lazy(() => import('./steps/BankAccountVerificationNotAbleToVerify'));
const CounterOffer = lazy(() => import('./steps/CounterOffer/CounterOffer'));

const componentByApplicationStep = {
  [ApplicationStep.Overview]: Overview,
  [ApplicationStep.ECOAConsent]: ImportantInformation,
  [ApplicationStep.BankAccountVerificationSelectMethod]: BankAccountVerificationSelectMethod,
  [ApplicationStep.BankAccountVerificationLoginConsent]: BankAccountVerificationLoginConsent,
  [ApplicationStep.BankAccountVerificationVerifyConsent]: BankAccountVerificationVerifyConsent,
  [ApplicationStep.BankAccountVerificationLoginWidget]: BankAccountVerificationLoginWidget,
  [ApplicationStep.BankAccountVerificationEnterInformation]: BankAccountVerificationEnterInformation,
  [ApplicationStep.BankAccountVerificationNotAbleToVerify]: BankAccountVerificationNotAbleToVerify,
  [ApplicationStep.IncomeSources]: IncomeSources,
  [ApplicationStep.DocumentsUpload]: DocumentsUpload,
  [ApplicationStep.SubmitApplication]: SubmitApplication,
  [ApplicationStep.Funding]: LoanComplete,
  [ApplicationStep.SigningDocuments]: LoanApproved,
  [ApplicationStep.ThankYou]: ThankYou,
  [ApplicationStep.Rejected]: ApplicationRejected,
  [ApplicationStep.ManualReview]: UnderReview,
  [ApplicationStep.AdditionalDocumentsUpload]: AdditionalDocumentsUpload,
  [ApplicationStep.ExpiredApplication]: ExpiredApplication,
  [ApplicationStep.CounterOffer]: CounterOffer,
};

export interface ApplicationStepProps {
  application: ApplicationInformation['application'];
  setApplicationStep: (step: ApplicationStep) => void;
}

const ApplicationProcess = () => {
  const { application, applicationFetching, applicationLoading, borrowerLoading } = useApplicationData();

  const { applicationStep, setApplicationStep } = useApplicationStep(application);

  if (applicationLoading || applicationFetching || borrowerLoading) {
    return <Loader />;
  }

  if (!applicationStep) {
    return <NoActiveApplication />;
  }

  const Component = componentByApplicationStep[applicationStep] || null;

  if (!Component) {
    return <NotFound />;
  }

  return (
    <Suspense fallback={<Loader />}>
      <Component setApplicationStep={setApplicationStep} application={application!} />
    </Suspense>
  );
};

export default ApplicationProcess;
